'use client';

import React, { useState, useEffect, useCallback } from 'react';
import posthog from 'posthog-js';
import { RefreshCw, Sparkles, ExternalLink, AlertTriangle, ChevronUp } from 'lucide-react';
import { NewsArticle, NewsResponse, NEWS_CONFIG, ArticleSummary } from '@/types/news';
import { TokenLimitModal } from './TokenLimitModal';
import { getApiBase } from '@/lib/apiBase';

interface NewsSectionProps {
  symbol: string;
  count?: number;
}

const SentimentBadge: React.FC<{ sentiment: NewsArticle['sentiment'] }> = ({
  sentiment,
}) => {
  const styles = {
    positive: 'bg-green-900/50 text-green-400',
    neutral: 'bg-[var(--color-surface-hover)] text-[var(--color-text-primary)]',
    negative: 'bg-red-900/50 text-red-400',
    unavailable: 'bg-yellow-900/40 text-yellow-500',
  };

  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles[sentiment]}`}
    >
      {sentiment === 'unavailable' ? 'sentiment unavailable' : sentiment}
    </span>
  );
};

const NewsCardSkeleton: React.FC = () => (
  <div className="rounded-xl bg-[var(--color-surface)] p-4 animate-pulse">
    <div className="flex-1 space-y-2">
      <div className="h-4 bg-[var(--color-surface-hover)] rounded w-3/4" />
      <div className="h-4 bg-[var(--color-surface-hover)] rounded w-1/2" />
      <div className="h-3 bg-[var(--color-surface-hover)] rounded w-1/3 mt-2" />
    </div>
  </div>
);

interface TokenLimitInfo {
  used: number;
  limit: number;
}

const NewsCard: React.FC<{
  article: NewsArticle;
  showSentiment: boolean;
  isPro: boolean;
  onTokenLimitExceeded: (info: TokenLimitInfo) => void;
}> = ({
  article,
  showSentiment,
  isPro,
  onTokenLimitExceeded,
}) => {
  const [summary, setSummary] = useState<ArticleSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const formattedDate = new Date(article.publishedAt).toLocaleDateString(
    'en-US',
    {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }
  );

  const handleSummarize = async () => {
    if (summary) {
      setExpanded(!expanded);
      return;
    }

    // Track news summary requested event
    posthog.capture('news_summary_requested', {
      article_title: article.title,
      article_source: article.source,
      article_sentiment: article.sentiment,
    });

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${getApiBase()}/api/news/summarize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          articleUrl: article.link,
          articleTitle: article.title,
          source: article.source,
        }),
      });

      if (response.status === 429) {
        const data = await response.json();
        onTokenLimitExceeded({ used: data.used, limit: data.limit });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to generate summary');
      }

      const data: ArticleSummary = await response.json();
      setSummary(data);
      setExpanded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate summary');
      posthog.captureException(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl bg-[var(--color-surface)] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h4 className="text-[var(--color-text)] font-medium text-sm leading-snug line-clamp-2">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-[var(--color-text-secondary)] text-xs">{article.source}</span>
            <span className="text-[var(--color-text-muted)] text-xs">•</span>
            <span className="text-[var(--color-text-muted)] text-xs">{formattedDate}</span>
          </div>
        </div>
        {showSentiment && <SentimentBadge sentiment={article.sentiment} />}
      </div>

      <div className="flex items-center gap-2 mt-3">
        {isPro && (
          <button
            onClick={handleSummarize}
            disabled={loading}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-[color-mix(in_srgb,var(--color-accent)_20%,transparent)] text-[var(--color-accent-muted)] hover:bg-[color-mix(in_srgb,var(--color-accent)_30%,transparent)] transition-colors disabled:opacity-50"
          >
            {loading ? (
              <>
                <RefreshCw size={12} className="animate-spin" />
                Summarizing...
              </>
            ) : expanded ? (
              <>
                <ChevronUp size={12} />
                Hide summary
              </>
            ) : (
              <>
                <Sparkles size={12} />
                Summarize
              </>
            )}
          </button>
        )}
        <a
          href={article.link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            posthog.capture('news_link_clicked', {
              article_title: article.title,
              article_source: article.source,
              article_sentiment: article.sentiment,
              article_url: article.link,
            });
          }}
          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-[var(--color-surface-hover)] text-[var(--color-text-primary)] hover:bg-[var(--color-toggle-active)] hover:text-[var(--color-text)] transition-colors"
        >
          <ExternalLink size={12} />
          View article
        </a>
      </div>

      {error && (
        <p className="text-red-400 text-xs mt-2">{error}</p>
      )}

      {expanded && summary && (
        <div className="mt-3 space-y-2">
          <div className="p-3 bg-[var(--color-surface-hover)]/50 rounded-xl border border-[var(--color-input)]">
            <p className="text-sm text-[var(--color-text-primary)] leading-relaxed">{summary.summary}</p>
          </div>
          <div className="flex items-start gap-2 p-2 bg-amber-900/20 rounded-md border border-amber-800/30">
            <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-400/90">
              AI-generated summary. Not financial advice. Verify all information independently.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export const NewsSection: React.FC<NewsSectionProps> = ({
  symbol,
  count = NEWS_CONFIG.defaultArticleCount,
}) => {
  const [news, setNews] = useState<NewsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [tokenLimitModal, setTokenLimitModal] = useState<TokenLimitInfo | null>(null);

  const handleTokenLimitExceeded = useCallback((info: TokenLimitInfo) => {
    setTokenLimitModal(info);
  }, []);

  const fetchNews = useCallback(
    async (forceRefresh = false) => {
      try {
        if (forceRefresh) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }
        setError(null);

        const url = `${getApiBase()}/api/news/${encodeURIComponent(symbol)}?count=${count}${forceRefresh ? '&refresh=1' : ''}`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error('Failed to fetch news');
        }

        const data: NewsResponse = await response.json();
        setNews(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch news');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [symbol, count]
  );

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  return (
    <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--color-surface)' }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">Latest News</h3>
        <button
          onClick={() => fetchNews(true)}
          disabled={refreshing}
          className="text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors disabled:opacity-50"
          title="Refresh news"
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      {loading && !news && (
        <div className="space-y-3">
          {Array.from({ length: count }).map((_, i) => (
            <NewsCardSkeleton key={i} />
          ))}
        </div>
      )}

      {error && !news && (
        <div className="text-center py-8">
          <p className="text-[var(--color-text-muted)] text-sm">{error}</p>
          <button
            onClick={() => fetchNews()}
            className="text-[var(--color-accent-muted)] hover:text-[var(--color-accent-muted)] text-sm mt-2"
          >
            Try again
          </button>
        </div>
      )}

      {news && news.articles?.length === 0 && (
        <p className="text-center text-[var(--color-text-muted)] text-sm py-8">
          No recent news found for {symbol}
        </p>
      )}

      {news && news.articles && news.articles.length > 0 && (
        <>
          <div className="space-y-3">
            {news.articles.map((article, i) => (
              <NewsCard
                key={`${article.link}-${i}`}
                article={article}
                showSentiment={news.hasSentiment || !!news.sentimentUnavailable}
                isPro={news.hasSentiment}
                onTokenLimitExceeded={handleTokenLimitExceeded}
              />
            ))}
          </div>

          {news.sentimentUnavailable && (
            <div className="mt-4 p-3 bg-yellow-900/20 rounded-xl border border-yellow-800/30">
              <p className="text-sm text-yellow-400/90">
                Sentiment analysis is temporarily unavailable. News will refresh automatically.
              </p>
            </div>
          )}

          {!news.hasSentiment && !news.sentimentUnavailable && (
            <div className="mt-4 p-3 bg-[var(--color-surface-hover)]/50 rounded-xl border border-[var(--color-input)]">
              <p className="text-sm text-[var(--color-text-primary)]">
                <span className="text-[var(--color-accent-muted)] font-medium">Upgrade to Pro</span>{' '}
                to unlock AI-powered sentiment analysis and article summaries.
              </p>
            </div>
          )}
        </>
      )}

      {news && (
        <p className="text-xs text-[var(--color-text-muted)] mt-4 text-center">
          Cached at {new Date(news.cachedAt).toLocaleTimeString()}
        </p>
      )}

      <TokenLimitModal
        isOpen={tokenLimitModal !== null}
        onClose={() => setTokenLimitModal(null)}
        used={tokenLimitModal?.used ?? 0}
        limit={tokenLimitModal?.limit ?? 0}
      />
    </div>
  );
};
